import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const { token } = useParams();
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [message, setMessage] = useState('');
  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await fetch(`${API}/api/public/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Verification failed');
        setStatus('success');
        setMessage(data.message);
        if (data.demo) setIsDemo(true);
      } catch (err) {
        setStatus('error');
        setMessage(err.message);
      }
    };
    verify();
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 px-6">
      <div className="mx-auto w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Link to="/">
            <img src="/pulse-logo-with-name.png" alt="Pulse by ProjectAI" className="h-24 object-contain" />
          </Link>
        </div>

        <Card className="shadow-lg border-slate-200">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {status === 'verifying' && 'Verifying your email...'}
              {status === 'success' && 'Email Verified!'}
              {status === 'error' && 'Verification Failed'}
            </CardTitle>
            <CardDescription>
              {status === 'verifying' && 'Please wait a moment.'}
              {status === 'success' && message}
              {status === 'error' && message}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            {status === 'verifying' && (
              <Loader2 className="h-12 w-12 text-[#5AA2AE] animate-spin" data-testid="verify-loading" />
            )}
            {status === 'success' && (
              <>
                <div className="h-16 w-16 rounded-full bg-green-50 flex items-center justify-center" data-testid="verify-success">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                <p className="text-sm text-slate-500 text-center">
                  The project administrator has been notified and will review your request shortly.
                </p>
                {isDemo && (
                  <div className="mt-2 p-4 bg-[#5AA2AE]/5 border border-[#5AA2AE]/20 rounded-lg text-center" data-testid="demo-user-notice">
                    <p className="text-sm font-medium text-[#5AA2AE]">
                      As a demo user, your invitation has been sent automatically!
                    </p>
                    <p className="text-sm text-slate-600 mt-1.5">
                      Check your email for an invitation link, create your account, and start exploring Pulse.
                    </p>
                  </div>
                )}
              </>
            )}
            {status === 'error' && (
              <>
                <div className="h-16 w-16 rounded-full bg-red-50 flex items-center justify-center" data-testid="verify-error">
                  <XCircle className="h-8 w-8 text-red-500" />
                </div>
                <p className="text-sm text-slate-500 text-center">
                  The link may have already been used or is invalid.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
